import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import message from 'antd/lib/message';
import Model from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Upload from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react';

import { axiosGetCall, axiosPostCall } from '../../api/axiosCall';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import { uploadFile, S3ImageFolder } from '../../utils/imageUploader';

const { Option } = Select;

function EditSparePart({
  isOpen,
  product,
  handleEditModelCloseClick,
  callback,
}) {
  let updatedProduct = { ...product };
  const [editForm] = Form.useForm();
  const { token } = useContext(AuthContext);
  const [productList, setProductList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(updatedProduct.productImage);
  const [isImageUploading, setIsImageUploading] = useState(false);

  useEffect(() => {
    getProductList();
  }, []);
  async function getProductList() {
    try {
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_ALL_PRODUCT,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.hideErrorFromUser) {
        setProductList(res.data);
      } else {
        message.error(res.error.message);
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  async function handleDeleteImageClick() {
    setImage('');
    setFileList([]);
  }

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
      setImage('');
    },
    beforeUpload: async (file) => {
      if (fileList.length < 1) {
        setFileList([...fileList, file]);
        // Automatically upload the image when selected
        try {
          setIsImageUploading(true);
          const uploadedImage = await uploadFile(
            file,
            S3ImageFolder.SubPart,
            token,
          );
          setImage(uploadedImage);
          setIsImageUploading(false);
          message.success('Image uploaded successfully');
        } catch (error) {
          setIsImageUploading(false);
          console.error('Image upload error:', error);

          // Provide more specific error messages based on error type
          let errorMessage = 'Failed to upload image';
          if (error.response) {
            // Server responded with error status
            if (error.response.status === 413) {
              errorMessage =
                'Image file is too large. Please select a smaller image.';
            } else if (error.response.status === 415) {
              errorMessage =
                'Invalid image format. Please select a valid image file.';
            } else if (error.response.status >= 500) {
              errorMessage =
                'Server error occurred while uploading image. Please try again.';
            } else if (error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          } else if (error.request) {
            // Network error
            errorMessage =
              'Network error. Please check your internet connection and try again.';
          } else if (error.message) {
            // Other error
            errorMessage = `Upload failed: ${error.message}`;
          }

          message.error(errorMessage);

          // Remove the file from fileList if upload fails
          const index = fileList.indexOf(file);
          const newFileList = fileList.slice();
          newFileList.splice(index, 1);
          setFileList(newFileList);
        }
      } else {
        message.error('Max image upload limit reached');
      }
      return false;
    },
    fileList,
  };

  const handleSaveClick = async () => {
    let formValues;
    try {
      formValues = await editForm.validateFields();
    } catch (err) {
      const firstError = err?.errorFields?.[0];
      if (firstError) {
        message.error(
          firstError.errors?.[0] || 'Please fill all required fields',
        );
        try {
          editForm.scrollToField(firstError.name);
        } catch (_) {}
      }
      return;
    }

    const { sparePartName, modelNo, productNames, gstTax, p1, p2, p3, r1 } =
      formValues;

    if (!image) {
      message.error('Please upload an image');
      return;
    }

    const productData = productList
      .filter((item) => {
        if (productNames.includes(item.productName)) return item;
      })
      .map((temp) => {
        return temp.productId;
      });
    try {
      setIsLoading(true);
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.UPDATE_SPARE_PART,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          productSparePartId: product.sparePartId,
          productIds: productData,
          productSparePartName: sparePartName,
          modelNo: modelNo,
          prices: {
            P1: p1,
            P2: p2,
            P3: p3,
            R1: r1,
          },
          gstTax: gstTax,
          isActive: true,
          userName: 'string',
          filesPaths: image,
        },
      });
      if (res?.hideErrorFromUser) {
        message.success(res.data.message);
        callback();
        setIsLoading(false);
        handleEditModelCloseClick();
        return;
      } else {
        message.error(res?.error?.message);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      message.error(SOMETHING_WENT_WRONG);
    }
  };

  return (
    <div>
      <Model
        open={isOpen}
        onCancel={handleEditModelCloseClick}
        closable={false}
        footer={null}
      >
        <div className='flex flex-col space-y-4'>
          <Row className='font-bold text-2xl self-center'>Edit Spare Part</Row>
          <div className='font-bold'>Images:</div>

          <div className='flex flex-row flex-wrap'>
            {image && (
              <div className='w-24 h-24 flex flex-col justify-between items-center m-2'>
                <img
                  src={image}
                  alt={`spare part`}
                  className=' w-20 h-20 object-contain border border-black'
                />
                <DeleteOutlined
                  className='pt-2'
                  onClick={() => handleDeleteImageClick()}
                />
              </div>
            )}
          </div>
          {!image && (
            <div className='flex flex-col'>
              <div className='flex flex-row space-x-3'>
                <div className='font-bold'>Add Product Image:</div>
                <Upload
                  accept='image/*'
                  {...uploadProps}
                  multiple={false}
                  maxCount={1}
                  disabled={isImageUploading}
                >
                  <Button
                    type='dashed'
                    disabled={isImageUploading}
                    icon={<UploadOutlined />}
                    className='border-primary text-primary'
                  >
                    {isImageUploading ? 'Uploading...' : 'Click to Upload'}
                  </Button>
                </Upload>
              </div>
            </div>
          )}
          <hr />
          <Form
            form={editForm}
            initialValues={updatedProduct}
          >
            <Form.Item
              label='Spare Part Name'
              name='sparePartName'
              rules={[
                { required: true, message: 'Please enter spare part name' },
              ]}
            >
              <Input name='sparePartName' />
            </Form.Item>
            <Form.Item
              label='Model Number'
              name='modelNo'
              rules={[{ required: true, message: 'Please enter model number' }]}
            >
              <Input name='modelNo' />
            </Form.Item>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='GST'
                name='gstTax'
                rules={[{ required: true, message: 'Please enter GST' }]}
              >
                <InputNumber name='gstTax' />
              </Form.Item>
            </Row>
            <span className='py-1 font-bold text-lg'>Prices:</span>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P1'
                name='p1'
                rules={[{ required: true, message: 'Please enter P1 price' }]}
              >
                <InputNumber name='p1' />
              </Form.Item>
              <Form.Item
                label='P2'
                name='p2'
                rules={[{ required: true, message: 'Please enter P2 price' }]}
              >
                <InputNumber name='p2' />
              </Form.Item>
            </Row>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P3'
                name='p3'
                rules={[{ required: true, message: 'Please enter P3 price' }]}
              >
                <InputNumber name='p3' />
              </Form.Item>
              <Form.Item
                label='R1'
                name='r1'
                rules={[{ required: true, message: 'Please enter R1 price' }]}
              >
                <InputNumber name='r1' />
              </Form.Item>
            </Row>
            <Form.Item
              label='Product'
              name='productNames'
              rules={[
                {
                  required: true,
                  type: 'array',
                  message: 'Please select at least one product',
                },
              ]}
            >
              <Select
                placeholder='Select a Product'
                showSearch
                mode='multiple'
              >
                {productList.map((product) => (
                  <Option
                    key={product.productId}
                    value={product.productName}
                  >
                    {product.productName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          <div className='flex flex-row justify-around'>
            <Button
              onClick={handleEditModelCloseClick}
              className='w-32 self-center bg-surface border border-border text-text hover:text-primary hover:border-primary'
              disabled={isLoading}
            >
              Close
            </Button>
            <Button
              onClick={handleSaveClick}
              className='w-32 self-center border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
              type='primary'
              disabled={isLoading || isImageUploading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </Model>
    </div>
  );
}

export default EditSparePart;
